import React from 'react';
import { Link } from 'react-router-dom';
import { CONTACT_INFO, TRUCK_SERVICES, COMPANY_INFO } from '../utils/constants';
import './Home.css';

const Home: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": COMPANY_INFO.name,
    "description": COMPANY_INFO.description,
    "url": COMPANY_INFO.website,
    "telephone": CONTACT_INFO.phone,
    "email": CONTACT_INFO.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CONTACT_INFO.address,
      "addressLocality": "Dubai",
      "addressCountry": "UAE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "25.2048",
      "longitude": "55.2708"
    },
    "openingHours": "Mo-Su 00:00-23:59",
    "priceRange": "$$",
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "25.2048",
        "longitude": "55.2708"
      },
      "geoRadius": "50000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Pickup Truck Rental Services",
      "itemListElement": TRUCK_SERVICES.map((service, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title,
          "description": service.description
        },
        "position": index + 1
      }))
    }
  };

  return (
    <div className="home-page">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-background">
            <img src="/pickup-rental-truck-dubai-home.jpeg" alt="Pickup Truck Rental Dubai" />
          </div>
          <div className="hero-content">
            <div className="container">
              <h1>Professional Pickup Truck Rental Services in Dubai</h1>
              <p>Leading pickup truck rental company offering <Link to="/1-ton-truck">1-10 ton trucks</Link> with tail lift for <Link to="/best-movers-packers-dubai-2025">home shifting</Link>, <Link to="/services">office moving</Link>, and delivery services. Available 24/7 across Dubai.</p>
              <div className="hero-buttons">
                <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">Call Now: {CONTACT_INFO.phone}</a>
                <Link to="/services" className="btn-secondary">View Our Services</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="services-overview">
          <div className="container">
            <h2>Our Pickup Truck Rental Services</h2>
            <p className="section-subtitle">We provide reliable and professional pickup truck rental services across Dubai with various capacity options</p>
            
            <div className="services-grid">
              {TRUCK_SERVICES.map((service) => (
                <div key={service.id} className="service-card">
                  <div className="service-image">
                    <img src={service.image} alt={service.title} />
                  </div>
                  <div className="service-content">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <ul className="service-features">
                      {service.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                    <div className="service-actions">
                      <Link to={`/${service.id}-truck`} className="service-btn">View Details</Link>
                      <a href={`tel:${CONTACT_INFO.phone}`} className="service-btn secondary">Book Now</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Links Section */}
        <section className="quick-links">
          <div className="container">
            <h2>Quick Access to Our Services</h2>
            <div className="quick-links-grid">
              <div className="quick-link-card">
                <h3>🚛 Truck Rental Services</h3>
                <ul>
                  <li><Link to="/1-ton-truck">1 Ton Pickup Truck Rental</Link></li>
                  <li><Link to="/3-ton-truck">3 Ton Pickup Truck Rental</Link></li>
                  <li><Link to="/5-ton-truck">5 Ton Pickup Truck Rental</Link></li>
                  <li><Link to="/7-ton-truck">7 Ton Pickup Truck Rental</Link></li>
                  <li><Link to="/10-ton-truck">10 Ton Pickup Truck Rental</Link></li>
                </ul>
              </div>
              <div className="quick-link-card">
                <h3>📚 Helpful Resources</h3>
                <ul>
                  <li><Link to="/pickup-truck-rental-guide-dubai">Complete Rental Guide</Link></li>
                  <li><Link to="/best-movers-packers-dubai-2025">Best Movers in Dubai</Link></li>
                  <li><Link to="/about">About Our Company</Link></li>
                  <li><Link to="/contact">Contact Information</Link></li>
                </ul>
              </div>
              <div className="quick-link-card">
                <h3>🏢 Service Areas</h3>
                <ul>
                  <li><Link to="/services">Downtown Dubai</Link></li>
                  <li><Link to="/services">Dubai Marina</Link></li>
                  <li><Link to="/services">Jumeirah</Link></li>
                  <li><Link to="/services">Business Bay</Link></li>
                  <li><Link to="/services">All Dubai Areas</Link></li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="why-choose-us">
          <div className="container">
            <h2>Why Choose Our Pickup Truck Rental Services?</h2>
            <div className="features-grid">
              <div className="feature-item">
                <div className="feature-icon">🚛</div>
                <h3>Modern Fleet</h3>
                <p>Well-maintained pickup trucks with tail lift for easy loading and unloading</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">⏰</div>
                <h3>24/7 Availability</h3>
                <p>Round-the-clock service to meet your urgent moving and delivery needs</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">👨‍💼</div>
                <h3>Professional Drivers</h3>
                <p>Experienced and licensed drivers for safe and efficient transportation</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">💰</div>
                <h3>Competitive Prices</h3>
                <p>Affordable rates with transparent pricing for all our truck rental services</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">🏠</div>
                <h3>Home & Office Shifting</h3>
                    <p>Specialized services for <Link to="/best-movers-packers-dubai-2025">residential and commercial moving</Link> requirements</p>
              </div>
              <div className="feature-item">
                <div className="feature-icon">📦</div>
                <h3>Delivery Services</h3>
                <p>Reliable delivery services for goods and packages across Dubai</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Need a Pickup Truck? Call Us Now!</h2>
              <p>Get instant quotes and book your <Link to="/services">pickup truck rental service</Link> in Dubai. Read our <Link to="/pickup-truck-rental-guide-dubai">complete rental guide</Link> for more information.</p>
              <div className="cta-buttons">
                <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">Call: {CONTACT_INFO.phone}</a>
                <a href={`mailto:${CONTACT_INFO.email}`} className="btn-secondary">Email Us</a>
              </div>
            </div>
          </div>
        </section>
      </div>
  );
};

export default Home;
